import { useState } from "react";
import { css, styled } from "styled-components";

import { JoinUsUpgradeBanner, SectionTitleUnderlined, UIToggle } from "common/components";
import { breakpoints } from "common/styles";
import { getCookie, setCookie } from "common/utilities/cookies";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { LatestKnowledgeFeed } from "domains/knowledgeFeed/components/LatestKnowledgeFeed";
import { BrowseKnowledgeFeed } from "domains/topicDetail/components/BrowseKnowledgeFeed";
import { TOPIC_FEED_SELECTED_VIEW, TopicFeedViews } from "domains/topicDetail/types";

const containerStyles = css`
  margin: 1rem;

  @media ${breakpoints.medium} {
    margin: 0 2rem 2rem 2rem;
  }

  @media ${breakpoints.large} {
    margin: 0 2rem 2rem 0;
  }

  @media ${breakpoints.extraLarge} {
    margin: 0 3rem 2rem 0;
  }
`;

const StyledContainer = styled.div`
  ${containerStyles};
`;

const StyledSectionTitleUnderlined = styled(SectionTitleUnderlined)`
  margin-bottom: 16px;
`;

const StyledUIToggle = styled(UIToggle)`
  margin-bottom: 16px;
`;

const StyledJoinUsUpgradeBanner = styled(JoinUsUpgradeBanner)`
  ${containerStyles};
`;

type TopicFeedProps = { topicId: string };

export const TopicFeed = ({ topicId }: TopicFeedProps) => {
  const track = useTrack();
  const translate = useTranslate();

  const initialSelectedView = (getCookie(TOPIC_FEED_SELECTED_VIEW) as TopicFeedViews) || TopicFeedViews.browse;
  const [selectedView, setSelectedView] = useState<TopicFeedViews>(initialSelectedView);

  const toggleView = () => {
    const newView = selectedView === TopicFeedViews.latest ? TopicFeedViews.browse : TopicFeedViews.latest;
    setCookie(TOPIC_FEED_SELECTED_VIEW, newView, "path=/; Max-Age=31536000"); // set to expire after a year
    setSelectedView(newView);
    track({ name: `Change to ${newView} topic view` });
  };

  const title =
    selectedView === TopicFeedViews.browse
      ? "app.topic.knowledge.browse_message"
      : "app.topic.knowledge.latest_message";

  return (
    <div>
      <StyledContainer>
        <StyledSectionTitleUnderlined>{translate(title)}</StyledSectionTitleUnderlined>
        <StyledUIToggle
          value={selectedView}
          onChange={toggleView}
          name="knowledge-toggle"
          items={[
            { text: translate("app.topic.knowledge.browse_message"), value: TopicFeedViews.browse },
            { text: translate("app.topic.knowledge.latest_message"), value: TopicFeedViews.latest },
          ]}
        />
      </StyledContainer>
      <StyledJoinUsUpgradeBanner trackPage="Topic detail" />
      {selectedView === TopicFeedViews.latest ? (
        <LatestKnowledgeFeed topicId={topicId} />
      ) : (
        <BrowseKnowledgeFeed topicId={topicId} />
      )}
    </div>
  );
};
